/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { message, Spin, Tag, Alert } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { usersApi } from "../../apis";
import TwoFactorSettings from "./TwoFactorSettings";

// ── helpers ───────────────────────────────────────────────────────────────────
const roleStr = (role) =>
  (typeof role === "string" ? role : role?.name || "").toLowerCase();
const initials = (u) =>
  `${u?.firstName?.[0] || ""}${u?.lastName?.[0] || ""}`.toUpperCase() || "U";

const STYLES = `
  .profile-avatar-ring {
    width: 100px; height: 100px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 36px; font-weight: 800; color: #fff;
    margin: 0 auto 16px;
    box-shadow: 0 8px 24px rgba(123,77,18,.25);
    flex-shrink: 0;
  }
  .profile-avatar--admin    { background: linear-gradient(135deg, #7B4D12, #8B5E1A); }
  .profile-avatar--customer { background: linear-gradient(135deg, #6B4226, #8B6340); }

  .profile-field-row {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .profile-field-row:last-child { border-bottom: none; }
  .profile-field-ico {
    width: 36px; height: 36px; flex-shrink: 0;
    background: #FDF3E3; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    color: #7B4D12; font-size: 16px;
  }
  .profile-field-label {
    font-size: 11px; font-weight: 700; letter-spacing: .05em;
    text-transform: uppercase; color: #9ca3af; margin-bottom: 2px;
  }
  .profile-field-value {
    font-size: 15px; font-weight: 600; color: #1a1d2e;
  }
  .profile-field-empty { color: #d1d5db; font-style: italic; font-weight: 400; }

  .profile-edit-input {
    width: 100%; border: 1.5px solid #ddd9f3; border-radius: 8px;
    padding: 8px 12px; font-size: 14px; color: #1a1d2e;
    font-family: inherit; outline: none;
    transition: border-color .18s, box-shadow .18s;
    box-sizing: border-box;
  }
  .profile-edit-input:focus {
    border-color: #7B4D12;
    box-shadow: 0 0 0 3px rgba(123,77,18,.12);
  }
  .profile-edit-input.err { border-color: #ef4444; }
`;

// ══════════════════════════════════════════════════════════════════════════════
const Profile = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const u = await usersApi.getProfile();
      setUser(u);
      setForm({
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        phone: u.phone || "",
      });
    } catch {
      message.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const updated = await usersApi.updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || undefined,
      });
      setUser(updated);
      // Sync name in localStorage so Header + Sidebar update instantly
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...stored,
          firstName: updated.firstName,
          lastName: updated.lastName,
          phone: updated.phone,
        }),
      );
      message.success("Profile updated successfully");
      setEditing(false);
    } catch (e) {
      const msg = e.response?.data?.message;
      if (Array.isArray(msg)) msg.forEach((m) => message.error(m));
      else message.error(msg || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setErrors({});
    setForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
    });
  };

  const isAdmin = roleStr(user?.role) === "admin";

  // ── Field helper ──────────────────────────────────────────────────────────
  const Field = ({
    icon,
    label,
    value,
    editKey,
    type = "text",
    readOnly = false,
  }) => (
    <div className="profile-field-row">
      <div className="profile-field-ico">{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="profile-field-label">{label}</div>
        {editing && editKey && !readOnly ? (
          <div>
            <input
              type={type}
              className={`profile-edit-input${errors[editKey] ? " err" : ""}`}
              value={form[editKey]}
              onChange={(e) =>
                setForm((f) => ({ ...f, [editKey]: e.target.value }))
              }
              disabled={saving}
            />
            {errors[editKey] && (
              <small style={{ color: "#ef4444", fontSize: 12 }}>
                {errors[editKey]}
              </small>
            )}
          </div>
        ) : (
          <div
            className={`profile-field-value${
              !value ? " profile-field-empty" : ""
            }`}
          >
            {value || "Not set"}
          </div>
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <style>{STYLES}</style>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>My Profile</h5>
              {!loading && !editing && isAdmin && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setEditing(true)}
                >
                  <EditOutlined style={{ marginRight: 6 }} />
                  Edit Profile
                </button>
              )}
              {editing && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary cancel"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <CloseOutlined style={{ marginRight: 6 }} />
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <SaveOutlined style={{ marginRight: 6 }} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          ) : !user ? (
            <Alert message="Failed to load profile." type="error" />
          ) : (
            <div className="row">
              {/* ── Left: Avatar + role card ── */}
              <div className="col-xl-4 col-lg-4 col-md-12 mb-4">
                <div className="card mb-0" style={{ borderRadius: 14 }}>
                  <div
                    className="card-body"
                    style={{ textAlign: "center", padding: "36px 24px" }}
                  >
                    {/* Avatar */}
                    <div
                      className={`profile-avatar-ring ${
                        isAdmin
                          ? "profile-avatar--admin"
                          : "profile-avatar--customer"
                      }`}
                    >
                      {initials(user)}
                    </div>

                    {/* Name */}
                    <h4
                      style={{
                        fontWeight: 700,
                        color: "#1a1d2e",
                        marginBottom: 4,
                      }}
                    >
                      {user.firstName} {user.lastName}
                    </h4>

                    {/* Email */}
                    <div
                      style={{
                        fontSize: 13,
                        color: "#9ca3af",
                        marginBottom: 16,
                      }}
                    >
                      {user.email}
                    </div>

                    {/* Role badge */}
                    <Tag
                      color={isAdmin ? "volcano" : "orange"}
                      style={{
                        fontSize: 13,
                        padding: "4px 14px",
                        borderRadius: 20,
                      }}
                    >
                      {isAdmin ? "Administrator" : "Customer"}
                    </Tag>

                    {/* Status */}
                    <div style={{ marginTop: 16 }}>
                      {user.isActive !== false ? (
                        <Tag
                          color="green"
                          icon={<CheckCircleOutlined />}
                          style={{ fontSize: 12 }}
                        >
                          Active Account
                        </Tag>
                      ) : (
                        <Tag color="default" style={{ fontSize: 12 }}>
                          Inactive
                        </Tag>
                      )}
                    </div>

                    {/* Member since */}
                    {user.createdAt && (
                      <div
                        style={{
                          marginTop: 20,
                          paddingTop: 16,
                          borderTop: "1px solid #f3f4f6",
                          fontSize: 12,
                          color: "#9ca3af",
                        }}
                      >
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        Member since{" "}
                        {new Date(user.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Right: Info + edit form ── */}
              <div className="col-xl-8 col-lg-8 col-md-12">
                <div className="card mb-4" style={{ borderRadius: 14 }}>
                  <div
                    className="card-body"
                    style={{ padding: "28px 28px 20px" }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: "#9ca3af",
                        marginBottom: 4,
                      }}
                    >
                      Personal Information
                    </div>

                    {editing && (
                      <Alert
                        message="You can update your first name, last name, and phone number. Email cannot be changed here."
                        type="info"
                        showIcon
                        className="mb-3"
                        style={{ borderRadius: 8 }}
                      />
                    )}

                    <Field
                      icon={<UserOutlined />}
                      label="First Name"
                      value={user.firstName}
                      editKey="firstName"
                    />
                    <Field
                      icon={<UserOutlined />}
                      label="Last Name"
                      value={user.lastName}
                      editKey="lastName"
                    />
                    <Field
                      icon={<MailOutlined />}
                      label="Email Address"
                      value={user.email}
                      readOnly
                    />
                    <Field
                      icon={<PhoneOutlined />}
                      label="Phone Number"
                      value={user.phone}
                      editKey="phone"
                      type="tel"
                    />
                  </div>
                </div>

                {/* Account details (read-only) */}
                <div className="card mb-0" style={{ borderRadius: 14 }}>
                  <div
                    className="card-body"
                    style={{ padding: "28px 28px 20px" }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: "#9ca3af",
                        marginBottom: 4,
                      }}
                    >
                      Account Details
                    </div>

                    <div className="profile-field-row">
                      <div
                        className="profile-field-ico"
                        style={{
                          background: isAdmin ? "#fee2e2" : "#FDF3E3",
                          color: isAdmin ? "#dc2626" : "#7B4D12",
                        }}
                      >
                        <UserOutlined />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="profile-field-label">Role</div>
                        <Tag
                          color={isAdmin ? "red" : "volcano"}
                          style={{ marginTop: 2, fontSize: 12 }}
                        >
                          {isAdmin ? "Admin" : "Customer"}
                        </Tag>
                      </div>
                    </div>

                    <div className="profile-field-row">
                      <div
                        className="profile-field-ico"
                        style={{
                          background:
                            user.isActive !== false ? "#dcfce7" : "#f3f4f6",
                          color:
                            user.isActive !== false ? "#16a34a" : "#9ca3af",
                        }}
                      >
                        <CheckCircleOutlined />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="profile-field-label">
                          Account Status
                        </div>
                        <Tag
                          color={user.isActive !== false ? "green" : "default"}
                          style={{ marginTop: 2, fontSize: 12 }}
                        >
                          {user.isActive !== false ? "Active" : "Inactive"}
                        </Tag>
                      </div>
                    </div>

                    {user.updatedAt && (
                      <div className="profile-field-row">
                        <div className="profile-field-ico">
                          <CalendarOutlined />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="profile-field-label">
                            Last Updated
                          </div>
                          <div className="profile-field-value">
                            {new Date(user.updatedAt).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <TwoFactorSettings />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
